import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IOffer } from '@rbi-ctg/menu';
import {
  IEvaluateUserOffersQuery,
  IOfferFeedbackEntryFragment,
  Locale,
  useEvaluateUserOffersQuery,
} from 'generated/rbi-graphql';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { useErrorContext } from 'state/errors';
import { useLocale } from 'state/intl';
import { useOffersContext } from 'state/offers';
import { mapOfferDataToNewOffer } from 'state/offers/util';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { platform } from 'utils/environment';
import { redeemedOnTime } from 'utils/offers';
import { routes } from 'utils/routing';

import { shouldShowCateringMessage } from '../should-show-catering-message';
import { shouldShowDeliveryMessage } from '../should-show-delivery-message';

import { IFeatureOffers, useFeatureOffers } from './use-feature-offers';

export interface IOffersUIContext {
  suffixUrl: string;
  offersUrl: string;
  showDeliveryMessage: boolean;
  showCateringMessage: boolean;
  loading: boolean;
  showEmptyState: boolean;
  showMissingOffer: boolean;
  showMobileBackButton: boolean;
  hideHeaderBar: boolean;
  authenticated: boolean;
  onSelectOffer: (offerId: string, isUpsell?: boolean) => void;
  previewOffer: IOffer | undefined;
  previewOfferId: string | null;
  previewOfferFeedback: IOfferFeedbackEntryFragment | null;
  offers: IOffer[];
  offersFeedback: Record<string, IOfferFeedbackEntryFragment> | undefined;
  refreshOffers: () => Promise<void>;
  featureOffers: IFeatureOffers | null;
  updateDetailsPanelContentHeight: (height: number) => void;
  togglePromoCodeOfferRedeemable: (offerId: string) => void;
}

export const OffersUIContext = createContext<IOffersUIContext>({} as IOffersUIContext);

export const useOffersUIContext = () => useContext(OffersUIContext);

interface IOffersUIProviderProps extends IBaseProps {
  suffixUrl: string;
  currentUrl: string;
  hideHeaderBar?: boolean;
  urlOfferId: string | null;
  updateDetailsPanelContentHeight: (height: number) => void;
}

export const OffersUIProvider: React.FC<IOffersUIProviderProps> = ({
  suffixUrl,
  currentUrl,
  hideHeaderBar = false,
  children,
  urlOfferId,
  updateDetailsPanelContentHeight,
}) => {
  const [redeemedOn, setRedeemedOn] = useState<string>(redeemedOnTime());
  const navigate = useNavigate();
  const { setSanityDataRef } = useErrorContext();
  const location = useLocation();
  const { language } = useLocale();
  const { isDelivery, isCatering } = useOrderContext();
  const {
    loading: offersLoading,
    offers,
    offersFeedback,
    refreshOffers,
    togglePromoCodeOfferRedeemable,
    redeemedOffer,
    selectedOffer,
    setUpsellOfferId,
  } = useOffersContext();
  const [previewOffer, setPreviewOffer] = useState<IOffer | undefined>(
    redeemedOffer || (urlOfferId ? offers.find(offer => offer._id === urlOfferId) : offers[0])
  );
  const { store } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const { isAuthenticated } = useAuthContext();
  const { featureOffersLoading, featureOffers } = useFeatureOffers();
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  useEffect(() => {
    // We don't have to preview offer at this point if there is no urlOfferId.
    if (isMobile && !urlOfferId) {
      setPreviewOffer(undefined);
    } else {
      setPreviewOffer(
        redeemedOffer || (urlOfferId ? offers.find(offer => offer._id === urlOfferId) : offers[0])
      );
    }
  }, [isMobile, offers, redeemedOffer, urlOfferId]);

  const { data: singleOfferData, loading: singleOfferLoading } = useEvaluateUserOffersQuery({
    skip: !!previewOffer,
    fetchPolicy: 'network-only',
    variables: {
      couponIds: [urlOfferId],
      locale: language as Locale,
      platform: platform(),
      serviceMode,
      redeemedOn,
      storeId: store.number,
    },
  });

  const updateSingleOffer = useCallback(
    (queryData: IEvaluateUserOffersQuery | undefined) => {
      if (!queryData?.evaluateUserOffers?.offersFeedback?.[0]?.offerDetails) {
        setPreviewOffer(undefined);
        return;
      }

      // Get all redeemable user offers in the same order as sortedOffers
      const { offersFeedback: offerFeedbackResult } = queryData.evaluateUserOffers;

      const mappedOffer = mapOfferDataToNewOffer({
        offerFeedback: offerFeedbackResult[0] as IOfferFeedbackEntryFragment,
        language,
      });

      setPreviewOffer(mappedOffer);
    },
    [language]
  );

  useEffect(() => {
    if (!singleOfferLoading && singleOfferData) {
      updateSingleOffer(singleOfferData);
    }
  }, [singleOfferData, updateSingleOffer, singleOfferLoading]);

  // set data ref as the offers and
  useEffect(() => {
    if (location.pathname.includes(routes.offers) && offers) {
      setSanityDataRef({ previewOffer, offers });
    }
  }, [offers, location.pathname, previewOffer, setSanityDataRef]);

  const previewOfferId = useMemo(() => previewOffer?._id || null, [previewOffer]);
  const previewOfferFeedback =
    previewOfferId && offersFeedback ? offersFeedback[previewOfferId] : null;

  const loading = offersLoading || featureOffersLoading;
  const showEmptyState = !loading && offers.length === 0 && !previewOffer;

  const showMissingOffer = !loading && !previewOffer && !singleOfferLoading;
  const showMobileBackButton = !!urlOfferId;
  const offersUrl = `${currentUrl}${suffixUrl}`;
  const authenticated = isAuthenticated();
  const showDeliveryMessage = shouldShowDeliveryMessage({
    offers,
    loading: offersLoading,
    isDelivery,
  });
  const showCateringMessage = shouldShowCateringMessage({
    offers,
    loading: offersLoading,
    isCatering,
  });

  const onSelectOffer = useCallback(
    (offerId: string, isUpsell = false) => {
      navigate(`${offersUrl}/${offerId}`);
      setUpsellOfferId(isUpsell ? offerId : null);
    },
    [navigate, offersUrl, setUpsellOfferId]
  );

  useEffect(() => {
    setRedeemedOn(redeemedOnTime());
  }, [offersUrl]);

  const contextValue: IOffersUIContext = useMemo(
    () => ({
      suffixUrl,
      offersUrl,
      showDeliveryMessage,
      showCateringMessage,
      hideHeaderBar,
      loading,
      showEmptyState,
      showMissingOffer,
      showMobileBackButton,
      authenticated,
      onSelectOffer,
      previewOffer,
      previewOfferId,
      previewOfferFeedback,
      offers,
      offersFeedback,
      refreshOffers,
      featureOffers,
      updateDetailsPanelContentHeight,
      togglePromoCodeOfferRedeemable,
      selectedOffer,
    }),
    [
      suffixUrl,
      offersUrl,
      showDeliveryMessage,
      showCateringMessage,
      hideHeaderBar,
      loading,
      showEmptyState,
      showMissingOffer,
      showMobileBackButton,
      authenticated,
      onSelectOffer,
      previewOffer,
      previewOfferId,
      previewOfferFeedback,
      offers,
      offersFeedback,
      refreshOffers,
      featureOffers,
      updateDetailsPanelContentHeight,
      togglePromoCodeOfferRedeemable,
      selectedOffer,
    ]
  );

  return <OffersUIContext.Provider value={contextValue}>{children}</OffersUIContext.Provider>;
};
